import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { GEO_MAP_SERVICE_ADDRESS } from "constants/app.constants";
import { SHAPEFILE_NAME_LABELS_LAYER } from "constants/mapLayers.constants";
import mapboxgl, { ImageSource } from "mapbox-gl";
import { RootState } from "store/rootReducer";
import { selectDisplaySettings, useMapStore } from "stores/map";
import { arrayOrEmpty } from "utils";

import { ShapefileFileType } from "models/projects";

import { useEditableProjectLayerList } from "components/project/layers/hooks";
import { useProjectContext } from "components/project/projects/context";
import { useCheckedShapefileList } from "components/project/shapefiles/hooks";
import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

import { MapLoadingSource } from "../../contexts/MapContextState";
import { useMapContext } from "../useMapContext";
import { useMapDispatch } from "../useMapDispatch";
import { useFetchTileJSON } from "./useFetchTileJSON";
import { onShapefilePropertyChanged, onShapefilesChanged } from "./utils";

export interface IUseManageMapboxShapefileLoading {
  addLayer;
  removeLayer;
}
export default function useManageMapboxShapefileLoading({
  addLayer
}: IUseManageMapboxShapefileLoading) {
  // Global state
  const shapefileFeatures = useSelector(
    (state: RootState) => state.project.shapefileFeatures
  );
  const displaySettings = useMapStore(selectDisplaySettings);

  const currentShapefileFeatureProperty = useSelector(
    (state: RootState) => state.project.currentShapefileFeatureProperty
  );
  const currentShapefileIdForFeatureProperty = useSelector(
    (state: RootState) => state.project.currentShapefileIdForFeatureProperty
  );

  // Context
  const { selectedProjectId, checkedLayerKeys } = useProjectContext();

  const { mapbox, isMapInitialized, isStyleLoaded } = useMapContext();
  const mapDispatch = useMapDispatch();

  // Hooks
  const layerList = useEditableProjectLayerList({ projectId: selectedProjectId });
  const shapefileList = useCheckedShapefileList();

  // Refs
  /**
   * Used to compare to the last state of the shapefiles to remove layers that are no longer checked
   * e.g Switching between projects, or checking shapefiles
   */
  const lastCheckedShapefiles = useRef<Set<string>>();

  const lastShapefileIdForFeatureProperty = useRef<string>();
  const lastShapefileFeatureProperty = useRef<string>();

  const shapefileListSorted = useMemo(() => {
    const checkedShapefiles = Array.from(layerList.checkedShapefiles);
    const sortedShapefileList = arrayOrEmpty(shapefileList.data).sort((a, b) => {
      const aId = a.projectShapefileId || a.shapefileId;
      const bId = b[aId === a.projectShapefileId ? "projectShapefileId" : "shapefileId"];
      const aIndex = checkedShapefiles.indexOf(aId);
      const bIndex = checkedShapefiles.indexOf(bId);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
    return sortedShapefileList || [];
  }, [shapefileList.data, layerList.checkedShapefiles]);

  const geoTiffUpdates = useMemo(
    () =>
      shapefileListSorted
        .filter((s) => s.fileType === ShapefileFileType.GeoTiff)
        .map((s) => ({
          layerName: s.projectShapefileId || s.shapefileId,
          shapefileId: s.shapefileId,
          projectShapefileId: s.projectShapefileId,
          renderingMode: s.renderingMode ?? "value"
        })),
    [shapefileListSorted]
  );

  const geoTiffRenderingModeKey = useMemo(
    () =>
      geoTiffUpdates
        .map(({ layerName, renderingMode }) => `${layerName}:${renderingMode}`)
        .join("|"),
    [geoTiffUpdates]
  );

  // Ref so the rendering mode effect can read the latest list without it as a dep
  const geoTiffUpdatesRef = useRef(geoTiffUpdates);
  geoTiffUpdatesRef.current = geoTiffUpdates;

  const prevRenderingModeKeyRef = useRef<string | null>(null);

  const GetTextOpacity = (shapefileId: string, checkedLayerKeys: string[]) => {
    const hasFeatures =
      Object.keys(shapefileFeatures).length > 0 &&
      shapefileFeatures[shapefileId]?.features?.length > 0;

    if (!hasFeatures) {
      return checkedLayerKeys.includes(SHAPEFILE_NAME_LABELS_LAYER) ? 1 : 0;
    }

    const uncheckedFeatures = shapefileFeatures[shapefileId]?.features
      .filter((f) => f.checked)
      .map((f) => f.shapefileGeomId);

    if (!uncheckedFeatures?.length) {
      return checkedLayerKeys.includes(SHAPEFILE_NAME_LABELS_LAYER) ? 1 : 0;
    }

    return checkedLayerKeys.includes(SHAPEFILE_NAME_LABELS_LAYER)
      ? ["match", ["get", "shapefileGeomId"], uncheckedFeatures, 1, 0]
      : 0;
  };
  useEffect(() => {
    return () => {
      lastCheckedShapefiles.current = new Set();
    };
  }, []);

  const tileJSONMap = useFetchTileJSON({
    mapbox,
    shapefileListSorted,
    layerList: layerList.data
  });

  useEffect(() => {
    if (!mapbox || !isStyleLoaded || !isMapInitialized) {
      return;
    }

    onShapefilesChanged({
      addLayer,
      checkedShapefiles: layerList.checkedShapefiles,
      lastCheckedShapefiles,
      layerList: layerList.data,
      mapbox,
      shapefileFeatures,
      shapefileListSorted,
      tileJSONMap
    });

    onShapefilePropertyChanged({
      addLayer,
      checkedLayerKeys,
      currentShapefileFeatureProperty,
      currentShapefileIdForFeatureProperty,
      lastShapefileFeatureProperty,
      lastShapefileIdForFeatureProperty,
      layerList: layerList.data,
      layerZoomSettings: displaySettings.layerZoomSettings,
      mapbox,
      shapefileFeatures,
      shapefileListSorted
    });
  }, [
    mapbox,
    isStyleLoaded,
    isMapInitialized,
    checkedLayerKeys,
    layerList.checkedShapefiles,
    layerList.data,
    shapefileFeatures,
    shapefileListSorted,
    currentShapefileFeatureProperty,
    currentShapefileIdForFeatureProperty,
    tileJSONMap,
    displaySettings.layerZoomSettings
  ]);

  useEffect(() => {
    if (!mapbox || !isStyleLoaded || !isMapInitialized) return;

    const prevKey = prevRenderingModeKeyRef.current;
    prevRenderingModeKeyRef.current = geoTiffRenderingModeKey;
    if (prevKey === null || prevKey === geoTiffRenderingModeKey) return;

    const pendingLayers = new Set<string>();

    for (const {
      layerName,
      shapefileId,
      projectShapefileId,
      renderingMode
    } of geoTiffUpdatesRef.current) {
      const source = mapbox.getSource(layerName) as ImageSource | undefined;
      if (!source) continue;

      const baseUrl = projectShapefileId
        ? `${GEO_MAP_SERVICE_ADDRESS}/geo-tiff/project-shapefile/${projectShapefileId}`
        : `${GEO_MAP_SERVICE_ADDRESS}/geo-tiff/${shapefileId}`;
      const url = `${baseUrl}?renderingMode=${renderingMode}&t=${Date.now()}`;
      source.updateImage({ url });

      pendingLayers.add(layerName);
    }

    if (pendingLayers.size === 0) return;

    mapDispatch({
      payload: { loadingSource: MapLoadingSource.Shapefile, isLoading: true }
    });

    const clearLoading = () => {
      mapDispatch({
        payload: { loadingSource: MapLoadingSource.Shapefile, isLoading: false }
      });
    };

    const onSourceData = (e: mapboxgl.MapSourceDataEvent) => {
      if (!pendingLayers.has(e.sourceId)) return;
      if (e.sourceDataType !== "content") return;
      pendingLayers.delete(e.sourceId);
      if (pendingLayers.size === 0) {
        clearLoading();
      }
    };

    const onError = () => {
      pendingLayers.clear();
      clearLoading();
    };

    mapbox.on("sourcedata", onSourceData);
    mapbox.on("error", onError);

    return () => {
      mapbox.off("sourcedata", onSourceData);
      mapbox.off("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapbox, isStyleLoaded, isMapInitialized, geoTiffRenderingModeKey, mapDispatch]);

  useEffect(() => {
    if (
      !mapbox ||
      !isStyleLoaded ||
      !isMapInitialized ||
      !mapbox.isStyleLoaded?.() ||
      !mapbox.getStyle()?.layers
    ) {
      return;
    }

    const mapboxLayers = mapbox.getStyle().layers;
    for (const layer of mapboxLayers) {
      if (layer.id.includes(`${shapefileLayerType.label}`)) {
        const shapefileId = layer.id.replace(`${shapefileLayerType.label}`, "");

        const textOpacity = GetTextOpacity(shapefileId, checkedLayerKeys);
        mapbox.setPaintProperty(layer.id, "text-opacity", textOpacity);
      }
    }
  }, [mapbox, isStyleLoaded, isMapInitialized, checkedLayerKeys]);

  /** Sync up shapefile list fetching with map loading.
   * This re-triggers sorting, as layer sorting is dependant on mapContext.isLoading
   * */
  useEffect(() => {
    mapDispatch({
      payload: {
        isLoading: shapefileList.isFetching || shapefileList.isLoading
      }
    });
  }, [shapefileList.isFetching, shapefileList.isLoading]);

  useEffect(() => {
    mapDispatch({
      payload: {
        mapAlert: shapefileList.isError
          ? "Unable to load shapefiles or GeoTIFFs. Please try again at a later time."
          : ""
      }
    });
  }, [shapefileList.isError, mapDispatch]);
}
